import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TwoFactorSetupResponse } from '@/shared/api';
import { useApi } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { PageHeader } from '@/shared/components';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';
import { TwoFactorBeginCard } from './TwoFactorBeginCard';
import { TwoFactorManagePanel } from './TwoFactorManagePanel';
import { TwoFactorSetupPanel } from './TwoFactorSetupPanel';
import { useSecurityRun } from './useSecurityRun';
import { useRecoveryCodes } from './useRecoveryCodes';

const statusKey = ['account-security', 'two-factor-status'] as const;
const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/** Kontosicherheit im Basis-Stil (Board „Kontosicherheit“): einrichten, Codes, ausschalten. */
export function AccountSecurityPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: statusKey, queryFn: api.accountSecurity.getTwoFactor });
  const problem = useApiProblem(status.error);
  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null);
  const {
    recoveryCodes,
    sessionFailure,
    receive: receiveRecoveryCodes,
    clear: clearRecoveryCodes,
  } = useRecoveryCodes();
  const [disableOpen, setDisableOpen] = useState(false);
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: statusKey });
  const enabled = status.data?.enabled;
  useEffect(() => {
    if (enabled === false) setDisableOpen(false);
  }, [enabled]);

  const { failure, setFailure, run, shown } = useSecurityRun(
    () => {
      setSetup(null);
      setDisableOpen(false);
    },
    () => void refreshStatus(),
  );
  const confirm = async (challengeToken: string, code: string) => {
    const result = await api.accountSecurity.confirmTwoFactorSetup(challengeToken, code);
    setSetup(null);
    await receiveRecoveryCodes(result);
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-3">
      <PageHeader
        className="mb-1"
        title="Sicherheit"
        description="Ein zweiter Faktor schützt dein Konto, auch wenn jemand dein Passwort kennt."
      />
      {failure?.unknown && (
        <p
          role="alert"
          className="bg-amber-tint text-amber-ink rounded-tile p-4 text-sm font-semibold"
        >
          {failure.text}
        </p>
      )}
      {status.isError && status.data && !setup && !recoveryCodes && (
        <div className={CARD}>
          <SurfaceStaleHint onRetry={() => void status.refetch()} retrying={status.isFetching} />
        </div>
      )}
      {recoveryCodes ? (
        <RecoveryCodesPanel
          codes={recoveryCodes}
          failure={sessionFailure ?? shown('confirm') ?? shown('regenerate')}
          onDone={() => {
            clearRecoveryCodes();
            setFailure(null);
            void refreshStatus();
          }}
        />
      ) : setup ? (
        <TwoFactorSetupPanel
          setup={setup}
          failure={shown('confirm')}
          onCancel={() => setSetup(null)}
          onConfirm={(code) => run('confirm', () => confirm(setup.challengeToken, code))}
        />
      ) : status.isPending ? (
        <div className={CARD}>
          <SurfaceLoading label="Sicherheitsstand wird geladen" rows={3} />
        </div>
      ) : !status.data ? (
        <div className={CARD}>
          <SurfaceLoadError
            message={`${problemText(problem, 'Der Sicherheitsstand konnte nicht geladen werden.')} Es wurde nichts verändert.`}
            onRetry={() => void status.refetch()}
            retrying={status.isFetching}
          />
        </div>
      ) : status.data.enabled ? (
        <TwoFactorManagePanel
          recoveryCodesRemaining={status.data.recoveryCodesRemaining}
          regenerateFailure={shown('regenerate')}
          disableFailure={shown('disable')}
          disableOpen={disableOpen}
          onDisableOpen={(open) => {
            setDisableOpen(open);
            if (!open) setFailure((current) => (current?.action === 'disable' ? null : current));
          }}
          onRegenerate={(password, code) =>
            run('regenerate', async () => {
              const result = await api.accountSecurity.regenerateRecoveryCodes({ password, code });
              await receiveRecoveryCodes(result);
            })
          }
          onDisable={(password, code) =>
            run('disable', async () => {
              await api.accountSecurity.disableTwoFactor({ password, code });
              setDisableOpen(false);
              toast.success('Zwei-Faktor-Anmeldung ausgeschaltet');
              await refreshStatus();
            })
          }
        />
      ) : (
        <TwoFactorBeginCard
          failure={shown('begin')}
          onBegin={(password) =>
            run('begin', async () =>
              setSetup(await api.accountSecurity.beginTwoFactorSetup(password)),
            )
          }
        />
      )}
    </div>
  );
}
